import LegalLayout from "./LegalLayout";
import { LEGAL, PRODUCT, isPlaceholder } from "../../lib/legal-config";

/**
 * Datenschutzerklärung nach DSGVO Art. 13 + 14 sowie § 19 TDDDG (Cookies /
 * Tracking, ehem. TTDSG). Stand: April 2026.
 *
 * Hier werden konkret die Verarbeitungen beschrieben, die Shadow Court
 * tatsächlich vornimmt — keine Boilerplate mit Google Analytics und
 * Facebook-Pixel, die wir gar nicht einsetzen.
 *
 * Kritisch sind vor allem:
 *   • Datenverarbeitung durch LLM-Unterauftragnehmer (OpenAI, DeepSeek)
 *     → Drittlandübermittlung → SCC-Klauseln + Art-49-Rechtfertigung
 *   • Rechtsgrundlage: Art. 6 Abs. 1 lit. b (Vertragserfüllung) für die
 *     eigentliche Jury-Simulation, lit. f (berechtigtes Interesse) für
 *     Rate-Limiting + Moderation, lit. a (Einwilligung) für Affiliate-
 *     Tracking-Cookie falls eines gesetzt wird.
 */
export default function Datenschutz() {
  return (
    <LegalLayout title="Datenschutzerklärung" lastUpdated="2026-04-22">
      <p>
        Diese Erklärung informiert Sie gemäß Art. 13 und 14 DSGVO über Art, Umfang und Zweck der
        Verarbeitung personenbezogener Daten im Rahmen von <strong>{PRODUCT.name}</strong>, einem
        Online-Dienst zur simulierten Urteilsprognose durch einen KI-gestützten Juroren-Schwarm.
      </p>

      <h2>1. Verantwortlicher</h2>
      <p>
        <strong>{isPlaceholder(LEGAL.companyName) ? "[PLACEHOLDER]" : LEGAL.companyName}</strong>
        <br />
        {isPlaceholder(LEGAL.streetAddress) ? "[PLACEHOLDER]" : LEGAL.streetAddress}
        <br />
        {isPlaceholder(LEGAL.postalCodeCity) ? "[PLACEHOLDER]" : LEGAL.postalCodeCity}, {LEGAL.country}
        <br />
        E-Mail: <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>
      </p>

      {LEGAL.dpo ? (
        <>
          <h3>Datenschutzbeauftragter</h3>
          <p>
            {LEGAL.dpo.name}, <a href={`mailto:${LEGAL.dpo.email}`}>{LEGAL.dpo.email}</a>
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-400">
          Ein Datenschutzbeauftragter ist nach Art. 37 DSGVO nicht bestellt, da die
          gesetzlichen Voraussetzungen hierfür nicht vorliegen.
        </p>
      )}

      <h2>2. Verarbeitete Datenkategorien und Zwecke</h2>

      <h3>a) Registrierung und Authentifizierung (Magic-Link)</h3>
      <p>
        Zum Anlegen eines Kontos erheben wir Ihre E-Mail-Adresse und einen Anzeigenamen. Nach
        Anforderung eines Login-Links senden wir eine E-Mail mit einem einmalig gültigen Token.
        Nach erfolgreicher Verifikation setzen wir ein Session-Cookie (JWT, HttpOnly, Secure,
        SameSite=None) mit einer Laufzeit von 30 Tagen.
      </p>
      <p>
        <em>Rechtsgrundlage:</em> Art. 6 Abs. 1 lit. b DSGVO (Durchführung des
        Nutzungsvertrags).
      </p>

      <h3>b) Fall-Uploads (PDF-Dokumente)</h3>
      <p>
        Von Ihnen hochgeladene PDF-Dokumente werden in einem verschlüsselten Objekt-Speicher
        (Cloudflare R2) abgelegt, serverseitig in Textabschnitte zerlegt und als Vektor-
        Repräsentationen in einem Vector-Index (Cloudflare Vectorize) gespeichert, damit der
        KI-Schwarm relevante Passagen im Kontext des Falls abrufen kann. Roh-Texte und
        Vektoren werden ausschließlich mit Ihrer Nutzer-ID verknüpft und nicht anderen Nutzern
        zugänglich gemacht.
      </p>
      <p>
        <em>Rechtsgrundlage:</em> Art. 6 Abs. 1 lit. b DSGVO. Wenn das Dokument personenbezogene
        Daten Dritter enthält (Gegenseite, Zeugen, etc.), ist die Verarbeitung regelmäßig auch
        durch Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer effektiven
        Rechtsverfolgung) gedeckt. Sie sind verpflichtet sicherzustellen, dass das Hochladen
        dieser Dokumente zulässig ist.
      </p>

      <h3>c) KI-Simulation (Mock-Jury)</h3>
      <p>
        Auf Ihre Anforderung hin wird der Fallkontext an eine Reihe von LLM-Inferenz-
        Dienstleistern übermittelt, die in Summe einen Juroren-Schwarm bilden. Vor der
        Übermittlung durchläuft der Text eine Moderationsprüfung und einen Sanitizer, um
        Prompt-Injection-Angriffe zu entschärfen.
      </p>
      <p>
        <em>Rechtsgrundlage:</em> Art. 6 Abs. 1 lit. b DSGVO.
      </p>

      <h3>d) Zahlungsabwicklung</h3>
      <p>
        Kostenpflichtige Funktionen werden über Stripe Payments Europe, Ltd. abgerechnet. Stripe
        erhebt dabei die für die Zahlung erforderlichen Daten (Name, E-Mail, Zahlungsmittel). Wir
        selbst erheben und speichern <strong>keine Kartendaten</strong>; wir erhalten von Stripe
        lediglich eine anonymisierte Transaktions-ID und den Zahlbetrag.
      </p>
      <p>
        <em>Rechtsgrundlage:</em> Art. 6 Abs. 1 lit. b DSGVO.
      </p>

      <h3>e) Affiliate-Programm</h3>
      <p>
        Wenn Sie sich als Affiliate registrieren, vergeben wir eine eindeutige Referrer-ID
        (ref_code). Klicks auf Ihren Affiliate-Link werden mit dieser ID sowie einem <strong>
        gehashten</strong> IP-Präfix (SHA-256) und dem User-Agent protokolliert — das Hashing
        verhindert, dass wir die ursprüngliche IP rekonstruieren können, erlaubt aber
        statistisch aussagekräftige Betrugs-Erkennung.
      </p>
      <p>
        <em>Rechtsgrundlage:</em> Art. 6 Abs. 1 lit. b DSGVO für die Provisionsberechnung;
        Art. 6 Abs. 1 lit. f DSGVO für die Betrugsprävention.
      </p>

      <h3>f) Server-Logs und Rate-Limiting</h3>
      <p>
        Beim Aufruf unserer Dienste protokolliert Cloudflare technisch zwingend erforderliche
        Zugangsdaten (Anfragezeit, IP-Adresse, User-Agent, aufgerufene URL). Diese dienen
        ausschließlich der Betriebssicherheit, der Abwehr von Missbrauch (Rate-Limit) und der
        Fehlersuche und werden nach 30 Tagen gelöscht bzw. anonymisiert.
      </p>
      <p>
        <em>Rechtsgrundlage:</em> Art. 6 Abs. 1 lit. f DSGVO.
      </p>

      <h2>3. Auftragsverarbeiter und Drittlandtransfer</h2>
      <p>
        Wir setzen die folgenden Auftragsverarbeiter und Unter-Auftragsverarbeiter gemäß
        Art. 28 DSGVO ein:
      </p>
      <ul>
        {PRODUCT.subProcessors.map((p) => (
          <li key={p.name}>
            <strong>{p.name}</strong> — {p.purpose}. Serverstandort: {p.location}.
          </li>
        ))}
      </ul>
      <p>
        Soweit eine Übermittlung in Drittländer erfolgt (insbesondere USA, China), stützen wir
        diese auf Standardvertragsklauseln der Europäischen Kommission gemäß Art. 46 Abs. 2 lit. c
        DSGVO bzw. ergänzende technische und organisatorische Maßnahmen. Für die Übermittlung an
        DeepSeek gilt zusätzlich, dass wir Nutzertexte vor der Übermittlung auf schädliche Inhalte
        prüfen und ein technischer Sanitizer angriffsrelevante Muster neutralisiert. Ein Widerruf
        ist jederzeit über{" "}
        <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> möglich; in diesem Fall
        können wir den KI-Schwarm jedoch nicht betreiben und das Vertragsverhältnis kann nicht
        aufrechterhalten werden.
      </p>

      <h2>4. Speicherdauer</h2>
      <ul>
        <li>Nutzerkonten: bis zum Löschen durch Sie oder nach 24 Monaten Inaktivität.</li>
        <li>Fall-PDFs und Vektor-Chunks: bis zum Löschen durch Sie oder 12 Monate.</li>
        <li>Abrechnungsdaten: 10 Jahre (§ 147 AO, handels- und steuerrechtliche Pflichten).</li>
        <li>Server-Logs: 30 Tage.</li>
        <li>Affiliate-Ledger: 10 Jahre (§ 147 AO).</li>
      </ul>

      <h2>5. Ihre Rechte</h2>
      <p>Sie haben jederzeit das Recht auf:</p>
      <ul>
        <li>Auskunft (Art. 15 DSGVO)</li>
        <li>Berichtigung (Art. 16 DSGVO)</li>
        <li>Löschung (Art. 17 DSGVO)</li>
        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
        <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
        <li>
          Widerspruch gegen Verarbeitung, die auf Art. 6 Abs. 1 lit. f DSGVO gestützt ist
          (Art. 21 DSGVO)
        </li>
        <li>Widerruf einer erteilten Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO)</li>
        <li>
          Beschwerde bei einer Aufsichtsbehörde, für uns zuständig ist die Landesbeauftragte für
          Datenschutz und Informationsfreiheit Nordrhein-Westfalen, Kavalleriestraße 2-4, 40213
          Düsseldorf, soweit die oben angegebene Adresse in NRW liegt — andernfalls die für Ihren
          Wohnsitz zuständige Behörde.
        </li>
      </ul>

      <h2>6. Cookies und ähnliche Technologien</h2>
      <p>
        Wir verwenden ausschließlich technisch notwendige Cookies im Sinne des § 25 Abs. 2 Nr. 2
        TDDDG. Dazu zählt insbesondere das Session-Cookie nach erfolgreichem Login. Eine
        Einwilligung ist hierfür nicht erforderlich. Analyse- oder Tracking-Cookies setzen wir
        nicht.
      </p>

      <h2>7. Automatisierte Entscheidungen</h2>
      <p>
        Die Urteilsprognosen werden automatisiert durch den KI-Schwarm erstellt, stellen jedoch
        <strong> keine automatisierte Entscheidung im Sinne des Art. 22 DSGVO</strong> dar,
        weil sie Ihnen lediglich als Entscheidungshilfe dienen und keine rechtlich bindende
        Folge gegen Sie entfalten. Die Letztentscheidung, ob Sie Ansprüche geltend machen, liegt
        ausschließlich bei Ihnen bzw. Ihrem Rechtsbeistand.
      </p>

      <h2>8. Änderungen dieser Datenschutzerklärung</h2>
      <p>
        Wir passen diese Erklärung an, wenn sich die technische oder rechtliche Grundlage unserer
        Verarbeitungen ändert. Die jeweils aktuelle Fassung ist unter dieser URL abrufbar. Bei
        wesentlichen Änderungen benachrichtigen wir eingeloggte Nutzer vorab per E-Mail.
      </p>
    </LegalLayout>
  );
}
